import "@/components/workspace/workspace.css";
import { ActivityPreview } from "@/components/ActivityFeed";
import { BatchActionBar } from "@/components/BatchActionBar";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { CatalogService } from "@/gen/omnios/v1/catalog_connect";
import type {
	CategoryPropertyLink,
	ProductCategory,
	PropertyDefinition,
} from "@/gen/omnios/v1/catalog_pb";
import { setEntityDragData as _setEntityDragData } from "@/lib/entity-context";
import { transport } from "@/lib/grpc";
import { useBreadcrumbs } from "@/lib/use-breadcrumbs";
import { useSlashFocus } from "@/lib/use-slash-focus";
import { createClient } from "@connectrpc/connect";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
	ArrowUpFromLine,
	Check,
	CheckSquare,
	ChevronDown,
	ChevronRight,
	Filter,
	Folder,
	FolderOpen,
	FolderPlus,
	FolderTree,
	Package,
	Pencil,
	Plus,
	Tag,
	Trash2,
	X,
} from "lucide-react";
import { MoreHorizontal } from "lucide-react";
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/catalog/categories")({
	component: CategoriesPage,
});

const client = createClient(CatalogService, transport);

function useValueTypes() {
	const { t } = useTranslation("categories");
	const VALUE_TYPES = [
		{ value: "string", label: t("vt_string") },
		{ value: "number", label: t("vt_number") },
		{ value: "boolean", label: t("vt_boolean") },
		{ value: "enum", label: t("vt_enum") },
	];
	const valueTypeLabel = (vt: string): string =>
		VALUE_TYPES.find((v) => v.value === vt)?.label ?? vt;
	return { VALUE_TYPES, valueTypeLabel };
}

// ── Page ──

function CategoriesPage() {
	const { t } = useTranslation("categories");

	useBreadcrumbs([{ label: t("title") }]);
	const qc = useQueryClient();
	const [selectedCat, setSelectedCat] = useState<ProductCategory | null>(null);
	const [selectMode, setSelectMode] = useState(false);
	const [checked, setChecked] = useState<Set<string>>(new Set());
	const [selectAll, setSelectAll] = useState(false);
	const [search, setSearch] = useState("");
	const searchRef = useRef<HTMLInputElement>(null);
	const clearSearch = useCallback(() => setSearch(""), []);
	useSlashFocus(searchRef, clearSearch);

	const { data, isLoading } = useQuery({
		queryKey: ["categories", "roots", search],
		queryFn: () =>
			client.listCategories({
				parentId: search.trim() ? "*" : "",
				search: search.trim(),
			}),
	});

	const { data: allCatsResp } = useQuery({
		queryKey: ["categories", "all"],
		queryFn: () => client.listCategories({ parentId: "*" }),
	});
	const totalCategoryCount = allCatsResp?.categories?.length ?? 0;

	const cats = data?.categories ?? [];

	const toggleCheck = (id: string) => {
		setSelectAll(false);
		setChecked((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const batchDeleteMut = useMutation({
		mutationFn: async (ids: string[]) => {
			for (const id of ids) {
				await client.deleteCategory({ id });
			}
		},
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["categories"],
			});
			setSelectedCat(null);
			setChecked(new Set());
		},
	});

	const checkedCount = checked.size;

	return (
		<div className="flex h-full overflow-hidden">
			<div className="w-1/2 min-w-[320px] border-r flex flex-col overflow-hidden">
				<div className="flex items-center gap-2 px-4 py-2 shrink-0">
					<div className="relative flex-1">
						<Tag className="absolute left-2 top-1.5 h-3.5 w-3.5 text-muted-foreground" />
						<Input
							ref={searchRef}
							placeholder={t("search_placeholder")}
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							className="pl-7 h-7 text-xs"
						/>
					</div>
					<Button
						variant={selectMode ? "secondary" : "ghost"}
						size="sm"
						className="h-7 text-xs shrink-0"
						disabled={cats.length === 0}
						onClick={() => {
							setSelectMode((v) => !v);
							setChecked(new Set());
						}}
					>
						<CheckSquare className="h-3 w-3 mr-1" />
						{t("select_btn")}
					</Button>
				</div>

				<div className="flex-1 overflow-y-auto">
					{isLoading && (
						<p className="py-8 text-center text-sm text-muted-foreground">
							{t("common:loading")}
						</p>
					)}

					{!isLoading && cats.length === 0 && (
						<div className="flex flex-col items-center justify-center py-16 px-6">
							<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted mb-4">
								<FolderTree className="h-5 w-5 text-muted-foreground" />
							</div>
							<h3 className="text-sm font-medium mb-1">{t("no_categories")}</h3>
							<p className="text-xs text-muted-foreground text-center max-w-xs mb-3">
								{t("create_or_import")}
							</p>
							<CreateCategoryInline parentId="" label={t("create_category")} />
						</div>
					)}

					{cats.length > 0 && (
						<>
							<div className="divide-y">
								{cats.map((cat) => (
									<CategoryNode
										key={cat.id}
										category={cat}
										level={0}
										selectedId={selectedCat?.id ?? null}
										onSelect={setSelectedCat}
										allCategories={cats}
										selectMode={selectMode}
										checked={checked}
										onToggleCheck={toggleCheck}
										selectAll={selectAll}
									/>
								))}
							</div>
							<div className="px-2 py-1">
								<CreateCategoryInline parentId="" label={t("root_category")} />
							</div>
						</>
					)}
				</div>
			</div>

			<div className="flex-1 min-w-0 overflow-hidden">
				{selectedCat ? (
					<CategoryDetailPanel
						key={selectedCat.id}
						category={selectedCat}
						onDeleted={() => setSelectedCat(null)}
					/>
				) : (
					<div className="flex flex-col items-center justify-center h-full text-center px-6">
						<FolderTree className="h-10 w-10 text-muted-foreground/30 mb-3" />
						<p className="text-sm font-medium text-muted-foreground">
							{t("select_category")}
						</p>
						<p className="text-xs text-muted-foreground/60 mt-1 max-w-xs">
							{t("select_category_hint")}
						</p>
					</div>
				)}
			</div>

			<BatchActionBar
				selectMode={selectMode}
				count={selectAll ? totalCategoryCount : checkedCount}
				total={totalCategoryCount}
				selectAll={selectAll}
				onSelectAll={() => {
					setSelectAll(true);
					setChecked(new Set());
				}}
				onClear={() => {
					setChecked(new Set());
					setSelectAll(false);
					setSelectMode(false);
				}}
				onDelete={() => {
					if (selectAll) {
						client.deleteAllCategories({}).then(() => {
							qc.invalidateQueries({ queryKey: ["categories"] });
							setChecked(new Set());
							setSelectAll(false);
							setSelectMode(false);
						});
					} else {
						batchDeleteMut.mutate([...checked]);
					}
				}}
				isPending={batchDeleteMut.isPending}
				entityLabel={t("entity_label")}
			/>
		</div>
	);
}

// ── Tree node ──

function CategoryNode({
	category,
	level,
	selectedId,
	onSelect,
	allCategories,
	selectMode,
	checked,
	onToggleCheck,
	selectAll,
}: {
	category: ProductCategory;
	level: number;
	selectedId: string | null;
	onSelect: (cat: ProductCategory | null) => void;
	allCategories: ProductCategory[];
	selectMode: boolean;
	checked: Set<string>;
	onToggleCheck: (id: string) => void;
	selectAll?: boolean;
}) {
	const qc = useQueryClient();
	const { t } = useTranslation("categories");
	const [expanded, setExpanded] = useState(false);
	const [renaming, setRenaming] = useState(false);
	const [renameName, setRenameName] = useState(category.name);
	const [moveOpen, setMoveOpen] = useState(false);
	const [moveTarget, setMoveTarget] = useState("");
	const isSelected = selectedId === category.id;

	const { data: children } = useQuery({
		queryKey: ["categories", "children", category.id],
		queryFn: () =>
			client.listCategories({
				parentId: category.id,
			}),
		enabled: expanded,
	});

	const { data: catProps } = useQuery({
		queryKey: ["category-properties", category.id],
		queryFn: () =>
			client.listCategoryProperties({
				categoryId: category.id,
			}),
	});

	const renameMut = useMutation({
		mutationFn: () =>
			client.updateCategory({
				id: category.id,
				name: renameName,
			}),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["categories"],
			});
			setRenaming(false);
		},
	});

	const deleteMut = useMutation({
		mutationFn: () =>
			client.deleteCategory({
				id: category.id,
			}),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["categories"],
			});
			onSelect(null);
		},
	});

	const moveMut = useMutation({
		mutationFn: () =>
			client.moveCategory({
				id: category.id,
				newParentId: moveTarget,
			}),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["categories"],
			});
			setMoveOpen(false);
		},
	});

	const propLinks = catProps?.links ?? [];
	const propCount = propLinks.length;

	return (
		<div>
			<div
				className={`group flex w-full items-center gap-1.5 px-3 py-1.5 text-sm transition-colors cursor-pointer cursor-grab ${
					isSelected ? "bg-accent text-accent-foreground" : "hover:bg-muted/50"
				}`}
				style={{
					paddingLeft: `${level * 16 + 8}px`,
				}}
				onClick={() => onSelect(isSelected ? null : category)}
				draggable
				onDragStart={(e) => {
					e.stopPropagation();
					_setEntityDragData(e, {
						entityType: "category",
						entityId: category.id,
						displayText: category.name,
					});
				}}
			>
				{selectMode && (
					<Checkbox
						checked={selectAll || checked.has(category.id)}
						onCheckedChange={() => onToggleCheck(category.id)}
						onClick={(e) => e.stopPropagation()}
						aria-label={t("select_name_aria", { name: category.name })}
						className="shrink-0"
					/>
				)}
				<Button
					variant="ghost"
					size="icon"
					className="h-5 w-5 text-muted-foreground shrink-0"
					onClick={(e) => {
						e.stopPropagation();
						setExpanded(!expanded);
					}}
				>
					{expanded ? (
						<ChevronDown className="h-3.5 w-3.5" />
					) : (
						<ChevronRight className="h-3.5 w-3.5" />
					)}
				</Button>
				{expanded ? (
					<FolderOpen className="h-4 w-4 text-amber-500 shrink-0" />
				) : (
					<Folder className="h-4 w-4 text-amber-500 shrink-0" />
				)}

				{renaming ? (
					<form
						className="flex items-center gap-1 flex-1"
						onSubmit={(e) => {
							e.preventDefault();
							renameMut.mutate();
						}}
					>
						<Input
							className="h-6 text-sm py-0"
							value={renameName}
							onChange={(e) => setRenameName(e.target.value)}
							autoFocus
							onClick={(e) => e.stopPropagation()}
						/>
						<Button
							type="submit"
							variant="ghost"
							size="sm"
							className="h-6 w-6 p-0"
						>
							<Check className="h-3 w-3" />
						</Button>
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="h-6 w-6 p-0"
							onClick={(e) => {
								e.stopPropagation();
								setRenaming(false);
								setRenameName(category.name);
							}}
						>
							<X className="h-3 w-3" />
						</Button>
					</form>
				) : (
					<span className="font-medium truncate flex-1">{category.name}</span>
				)}

				{propCount > 0 && !renaming && (
					<Badge
						variant="secondary"
						className="text-[10px] font-normal gap-0.5 shrink-0"
					>
						<Tag className="h-2.5 w-2.5" />
						{propCount}
					</Badge>
				)}

				{!renaming && (
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button
								variant="ghost"
								size="icon"
								className="h-5 w-5 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100"
								onClick={(e) => e.stopPropagation()}
							>
								<MoreHorizontal className="h-3.5 w-3.5" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem
								onSelect={() => {
									setRenameName(category.name);
									setRenaming(true);
								}}
							>
								<Pencil className="h-3.5 w-3.5 mr-2" />
								{t("rename")}
							</DropdownMenuItem>
							<DropdownMenuItem
								onSelect={() => {
									setExpanded(true);
								}}
							>
								<FolderPlus className="h-3.5 w-3.5 mr-2" />
								{t("subcategory")}
							</DropdownMenuItem>
							<DropdownMenuItem onSelect={() => setMoveOpen(true)}>
								<ArrowUpFromLine className="h-3.5 w-3.5 mr-2" />
								{t("move")}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem
								className="text-destructive focus:text-destructive"
								onSelect={() => deleteMut.mutate()}
							>
								<Trash2 className="h-3.5 w-3.5 mr-2" />
								{t("common:delete")}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>

			{/* Move dialog */}
			<Dialog open={moveOpen} onOpenChange={setMoveOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							{t("move_title", { name: category.name })}
						</DialogTitle>
					</DialogHeader>
					<Select value={moveTarget} onValueChange={setMoveTarget}>
						<SelectTrigger>
							<SelectValue placeholder={t("parent_category")} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="root">{t("root")}</SelectItem>
							{allCategories
								.filter((c) => c.id !== category.id)
								.map((c) => (
									<SelectItem key={c.id} value={c.id}>
										{c.path ? `${c.path} / ${c.name}` : c.name}
									</SelectItem>
								))}
						</SelectContent>
					</Select>
					<DialogFooter>
						<Button
							size="sm"
							disabled={!moveTarget || moveMut.isPending}
							onClick={() => moveMut.mutate()}
						>
							{t("move")}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{expanded && children && (
				<div>
					{children.categories.map((child) => (
						<CategoryNode
							key={child.id}
							category={child}
							level={level + 1}
							selectedId={selectedId}
							onSelect={onSelect}
							allCategories={allCategories}
							selectMode={selectMode}
							checked={checked}
							onToggleCheck={onToggleCheck}
							selectAll={selectAll}
						/>
					))}
					<div
						style={{
							paddingLeft: `${(level + 1) * 16 + 8}px`,
						}}
					>
						<CreateCategoryInline
							parentId={category.id}
							label={t("subcategory")}
						/>
					</div>
				</div>
			)}
		</div>
	);
}

// ── Detail panel ──

function CategoryDetailPanel({
	category,
	onDeleted,
}: {
	category: ProductCategory;
	onDeleted: () => void;
}) {
	const categoryId = category.id;
	const qc = useQueryClient();
	const { t } = useTranslation("categories");
	const [renaming, setRenaming] = useState(false);
	const [renameName, setRenameName] = useState("");

	const { data: catProps } = useQuery({
		queryKey: ["category-properties", categoryId],
		queryFn: () =>
			client.listCategoryProperties({
				categoryId,
			}),
	});

	const propDefs = catProps?.definitions ?? [];
	const propLinks = catProps?.links ?? [];
	const directLinks = propLinks.filter((l) => !l.inherited);
	const inheritedLinks = propLinks.filter((l) => l.inherited);

	const defById = new Map(propDefs.map((d) => [d.id, d]));

	const renameMut = useMutation({
		mutationFn: () =>
			client.updateCategory({
				id: categoryId,
				name: renameName,
			}),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["categories"],
			});
			setRenaming(false);
		},
	});

	const deleteMut = useMutation({
		mutationFn: () =>
			client.deleteCategory({
				id: categoryId,
			}),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["categories"],
			});
			onDeleted();
		},
	});

	const removePropMut = useMutation({
		mutationFn: (propDefId: string) => {
			const remaining = directLinks.filter(
				(l) => l.propertyDefinitionId !== propDefId,
			);
			return client.setCategoryProperties({
				categoryId,
				properties: remaining.map((l, i) => ({
					categoryId,
					propertyDefinitionId: l.propertyDefinitionId,
					isRequired: l.isRequired,
					sortOrder: i,
					inherited: false,
					sourceCategoryId: "",
					sourceCategoryName: "",
				})),
			});
		},
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["category-properties", categoryId],
			});
			qc.invalidateQueries({
				queryKey: ["category-properties"],
			});
		},
	});

	const toggleFilterMut = useMutation({
		mutationFn: ({
			id,
			isFilterable,
		}: {
			id: string;
			isFilterable: boolean;
		}) =>
			client.updatePropertyDefinition({
				id,
				isFilterable,
			}),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["category-properties", categoryId],
			});
			qc.invalidateQueries({
				queryKey: ["property-definitions"],
			});
		},
	});

	return (
		<div className="flex flex-col h-full overflow-hidden">
			{/* Header */}
			<div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
				{renaming ? (
					<div className="flex items-center gap-2 flex-1 min-w-0">
						<Input
							autoFocus
							value={renameName}
							onChange={(e) => setRenameName(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === "Enter" && renameName) {
									renameMut.mutate();
								}
								if (e.key === "Escape") {
									setRenaming(false);
								}
							}}
							className="h-8 text-sm max-w-xs"
						/>
						<Button
							size="sm"
							className="h-8 px-2"
							disabled={!renameName || renameMut.isPending}
							onClick={() => renameMut.mutate()}
						>
							<Check className="h-3.5 w-3.5" />
						</Button>
						<Button
							variant="ghost"
							size="sm"
							className="h-8 px-2"
							onClick={() => setRenaming(false)}
						>
							<X className="h-3.5 w-3.5" />
						</Button>
					</div>
				) : (
					<div className="flex items-center gap-2 min-w-0">
						<FolderOpen className="h-4 w-4 text-amber-500 shrink-0" />
						<h2 className="text-sm font-semibold truncate">{category.name}</h2>
						<Button
							variant="ghost"
							size="icon"
							className="h-5 w-5 text-muted-foreground hover:text-foreground shrink-0"
							onClick={() => {
								setRenameName(category.name);
								setRenaming(true);
							}}
						>
							<Pencil className="h-3 w-3" />
						</Button>
						<Link
							to="/catalog/products"
							search={{ owner_party_id: undefined }}
							className="ml-auto flex items-center gap-1 rounded-md px-2 py-1 text-xs text-muted-foreground hover:text-foreground hover:bg-accent transition-colors shrink-0"
						>
							<Package className="h-3 w-3" />
							{t("products_link")}
						</Link>
					</div>
				)}
			</div>

			{/* Scrollable body */}
			<div className="flex-1 overflow-y-auto">
				<div className="px-5 py-4 space-y-6">
					{/* Meta */}
					{(category.slug || category.path) && (
						<div className="space-y-1.5">
							{category.slug && (
								<div className="flex items-center gap-2 text-xs">
									<span className="text-muted-foreground w-16 shrink-0">
										Slug
									</span>
									<code className="font-mono text-muted-foreground">
										{category.slug}
									</code>
								</div>
							)}
							{category.path && (
								<div className="flex items-center gap-2 text-xs">
									<span className="text-muted-foreground w-16 shrink-0">
										{t("path_label")}
									</span>
									<span className="text-muted-foreground truncate">
										{category.path}
									</span>
								</div>
							)}
						</div>
					)}

					<Separator />

					{/* Inherited properties */}
					{inheritedLinks.length > 0 && (
						<section className="space-y-2">
							<h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								{t("inherited")}
							</h3>
							<div className="space-y-1">
								{inheritedLinks.map((link) => {
									const def = defById.get(link.propertyDefinitionId);
									if (!def) return null;
									return (
										<PropertyRow
											key={def.id}
											def={def}
											inherited
											sourceName={link.sourceCategoryName}
										/>
									);
								})}
							</div>
						</section>
					)}

					{/* Direct properties */}
					<section className="space-y-2">
						<div className="flex items-center justify-between">
							<h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
								{t("properties")}
								{directLinks.length > 0 && (
									<span className="ml-1 text-muted-foreground/50 normal-case tracking-normal">
										({directLinks.length})
									</span>
								)}
							</h3>
							<AddPropertyButton
								categoryId={categoryId}
								directLinks={directLinks}
								allPropLinks={propLinks}
								onChanged={() => {
									qc.invalidateQueries({
										queryKey: ["category-properties", categoryId],
									});
									qc.invalidateQueries({
										queryKey: ["category-properties"],
									});
								}}
							/>
						</div>

						{directLinks.length === 0 && (
							<p className="text-xs text-muted-foreground py-2">
								{t("no_direct_props")}
							</p>
						)}

						<div className="space-y-1">
							{directLinks.map((link) => {
								const def = defById.get(link.propertyDefinitionId);
								if (!def) return null;
								return (
									<PropertyRow
										key={def.id}
										def={def}
										onToggleFilter={() =>
											toggleFilterMut.mutate({
												id: def.id,
												isFilterable: !def.isFilterable,
											})
										}
										onRemove={() => removePropMut.mutate(def.id)}
									/>
								);
							})}
						</div>
					</section>

					<Separator />

					<ActivityPreview entityType="category" entityId={categoryId} />

					<Separator />

					{/* Actions */}
					<section className="space-y-2">
						<h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
							{t("actions_label")}
						</h3>
						<AlertDialog>
							<AlertDialogTrigger asChild>
								<Button
									variant="outline"
									size="sm"
									disabled={deleteMut.isPending}
									className="text-muted-foreground hover:text-destructive border-border hover:border-destructive/20 hover:bg-destructive/5 text-xs"
								>
									<Trash2 className="h-3 w-3 mr-1" />
									{t("delete_category")}
								</Button>
							</AlertDialogTrigger>
							<AlertDialogContent>
								<AlertDialogHeader>
									<AlertDialogTitle>
										{t("delete_confirm", { name: category.name })}
									</AlertDialogTitle>
									<AlertDialogDescription>
										{t("delete_hint")}
									</AlertDialogDescription>
								</AlertDialogHeader>
								<AlertDialogFooter>
									<AlertDialogCancel>{t("common:cancel")}</AlertDialogCancel>
									<AlertDialogAction
										onClick={() => deleteMut.mutate()}
										className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
									>
										{t("common:delete")}
									</AlertDialogAction>
								</AlertDialogFooter>
							</AlertDialogContent>
						</AlertDialog>
					</section>

					<div className="text-[10px] text-muted-foreground/40 font-mono pt-2">
						{categoryId}
					</div>
				</div>
			</div>
		</div>
	);
}

// ── Property row ──

function PropertyRow({
	def,
	inherited,
	sourceName,
	onToggleFilter,
	onRemove,
}: {
	def: PropertyDefinition;
	inherited?: boolean;
	sourceName?: string;
	onToggleFilter?: () => void;
	onRemove?: () => void;
}) {
	const { t } = useTranslation("categories");
	const { valueTypeLabel } = useValueTypes();
	return (
		<div
			className={`group flex items-center gap-2 rounded-md px-2.5 py-1.5 text-xs transition-colors ${
				inherited ? "bg-muted/30" : "bg-muted/50 hover:bg-muted"
			}`}
		>
			{inherited ? (
				<ArrowUpFromLine className="h-3 w-3 text-muted-foreground/50 shrink-0" />
			) : (
				<Tag className="h-3 w-3 text-muted-foreground shrink-0" />
			)}
			<span
				className={`font-medium truncate ${inherited ? "text-muted-foreground" : ""}`}
			>
				{def.name}
			</span>
			{def.unit && (
				<span className="text-muted-foreground/60 shrink-0">{def.unit}</span>
			)}
			<Badge variant="outline" className="text-[9px] h-4 shrink-0">
				{valueTypeLabel(def.valueType)}
			</Badge>
			{def.isFilterable && (
				<Filter className="h-3 w-3 text-blue-500 shrink-0" />
			)}
			{inherited && sourceName && (
				<span className="text-muted-foreground/40 text-[10px] truncate ml-auto">
					{t("from_source", { name: sourceName })}
				</span>
			)}
			{!inherited && (
				<div className="flex items-center gap-0.5 ml-auto opacity-0 group-hover:opacity-100 transition-opacity shrink-0">
					{onToggleFilter && (
						<Button
							variant="ghost"
							size="icon"
							title={
								def.isFilterable
									? t("remove_from_filters")
									: t("make_filterable")
							}
							className="h-5 w-5 text-muted-foreground hover:text-foreground"
							onClick={onToggleFilter}
						>
							<Filter className="h-3 w-3" />
						</Button>
					)}
					{onRemove && (
						<Button
							variant="ghost"
							size="icon"
							title={t("remove_from_category")}
							className="h-5 w-5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
							onClick={onRemove}
						>
							<X className="h-3 w-3" />
						</Button>
					)}
				</div>
			)}
		</div>
	);
}

// ── Inline create ──

function CreateCategoryInline({
	parentId,
	label,
}: {
	parentId: string;
	label: string;
}) {
	const qc = useQueryClient();
	const { t } = useTranslation("categories");
	const [open, setOpen] = useState(false);
	const [name, setName] = useState("");

	const createMut = useMutation({
		mutationFn: () =>
			client.createCategory({
				parentId,
				name,
				slug: "",
			}),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: ["categories"],
			});
			setName("");
			setOpen(false);
		},
	});

	if (!open) {
		return (
			<Button
				variant="ghost"
				size="sm"
				className="text-xs text-muted-foreground h-7"
				onClick={() => setOpen(true)}
			>
				<FolderPlus className="h-3.5 w-3.5 mr-1" />
				{label}
			</Button>
		);
	}

	return (
		<div className="flex items-center gap-2 py-1">
			<Input
				autoFocus
				placeholder={t("common:name")}
				value={name}
				onChange={(e) => setName(e.target.value)}
				onKeyDown={(e) => {
					if (e.key === "Enter" && name) {
						createMut.mutate();
					}
					if (e.key === "Escape") {
						setOpen(false);
					}
				}}
				className="h-7 text-xs max-w-[200px]"
			/>
			<Button
				size="sm"
				className="h-7 px-2"
				disabled={!name || createMut.isPending}
				onClick={() => createMut.mutate()}
			>
				<Check className="h-3.5 w-3.5" />
			</Button>
			<Button
				variant="ghost"
				size="sm"
				className="h-7 px-2"
				onClick={() => setOpen(false)}
			>
				<X className="h-3.5 w-3.5" />
			</Button>
			{createMut.isError && (
				<span className="text-xs text-destructive">
					{createMut.error?.message ?? t("common:error_generic")}
				</span>
			)}
		</div>
	);
}

// ── Add property button + panels ──

function AddPropertyButton({
	categoryId,
	directLinks,
	allPropLinks,
	onChanged,
}: {
	categoryId: string;
	directLinks: CategoryPropertyLink[];
	allPropLinks: CategoryPropertyLink[];
	onChanged: () => void;
}) {
	const qc = useQueryClient();
	const { t } = useTranslation("categories");
	const [open, setOpen] = useState(false);
	const [mode, setMode] = useState<"pick" | "create">("pick");

	const linkedIds = new Set(allPropLinks.map((l) => l.propertyDefinitionId));

	const addExistingMut = useMutation({
		mutationFn: (propDefId: string) => {
			const newLinks = [
				...directLinks.map((l, i) => ({
					categoryId,
					propertyDefinitionId: l.propertyDefinitionId,
					isRequired: l.isRequired,
					sortOrder: i,
					inherited: false,
					sourceCategoryId: "",
					sourceCategoryName: "",
				})),
				{
					categoryId,
					propertyDefinitionId: propDefId,
					isRequired: false,
					sortOrder: directLinks.length,
					inherited: false,
					sourceCategoryId: "",
					sourceCategoryName: "",
				},
			];
			return client.setCategoryProperties({
				categoryId,
				properties: newLinks,
			});
		},
		onSuccess: () => {
			onChanged();
			qc.invalidateQueries({
				queryKey: ["category-properties"],
			});
		},
	});

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button variant="outline" size="sm" className="h-7 text-xs">
					<Plus className="h-3 w-3 mr-1" />
					{t("common:add")}
				</Button>
			</PopoverTrigger>
			<PopoverContent align="end" className="w-72 p-0">
				<div className="flex border-b text-xs">
					<Button
						type="button"
						variant="ghost"
						className={`flex-1 rounded-none px-3 py-2 h-auto text-xs ${
							mode === "pick" ? "bg-muted font-medium" : "hover:bg-muted/50"
						}`}
						onClick={() => setMode("pick")}
					>
						{t("existing")}
					</Button>
					<Button
						type="button"
						variant="ghost"
						className={`flex-1 rounded-none px-3 py-2 h-auto text-xs ${
							mode === "create" ? "bg-muted font-medium" : "hover:bg-muted/50"
						}`}
						onClick={() => setMode("create")}
					>
						{t("create_new")}
					</Button>
				</div>

				{mode === "pick" && (
					<PickExistingProperty
						excludeIds={linkedIds}
						onPick={(id) => {
							addExistingMut.mutate(id);
							setOpen(false);
						}}
					/>
				)}

				{mode === "create" && (
					<CreateAndLinkProperty
						categoryId={categoryId}
						directLinks={directLinks}
						onDone={() => {
							onChanged();
							qc.invalidateQueries({
								queryKey: ["category-properties"],
							});
							qc.invalidateQueries({
								queryKey: ["property-definitions"],
							});
							setOpen(false);
						}}
					/>
				)}
			</PopoverContent>
		</Popover>
	);
}

function PickExistingProperty({
	excludeIds,
	onPick,
}: {
	excludeIds: Set<string>;
	onPick: (id: string) => void;
}) {
	const { t } = useTranslation("categories");
	const { valueTypeLabel } = useValueTypes();
	const [search, setSearch] = useState("");

	const { data: searchResp } = useQuery({
		queryKey: ["property-definitions", "search", search],
		queryFn: () =>
			client.listPropertyDefinitions({
				categoryId: "",
				search: search.trim(),
			}),
	});
	const filtered = (searchResp?.definitions ?? []).filter(
		(d) => !excludeIds.has(d.id),
	);

	return (
		<div className="p-2 space-y-1">
			<Input
				placeholder={t("common:search")}
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				className="h-7 text-xs"
				autoFocus
			/>
			<div className="max-h-48 overflow-y-auto space-y-0.5">
				{filtered.length === 0 && (
					<p className="text-xs text-muted-foreground text-center py-3">
						{t("common:no_results")}
					</p>
				)}
				{filtered.map((def) => (
					<Button
						key={def.id}
						variant="ghost"
						size="sm"
						className="flex w-full items-center gap-2 px-2 py-1.5 h-auto text-xs justify-start"
						onClick={() => onPick(def.id)}
					>
						<Tag className="h-3 w-3 text-muted-foreground shrink-0" />
						<span className="truncate font-medium">{def.name}</span>
						<span className="text-muted-foreground shrink-0">
							{valueTypeLabel(def.valueType)}
						</span>
						{def.unit && (
							<span className="text-muted-foreground/60 shrink-0">
								{def.unit}
							</span>
						)}
						{def.isFilterable && (
							<Filter className="h-2.5 w-2.5 text-blue-500 shrink-0" />
						)}
					</Button>
				))}
			</div>
		</div>
	);
}

function CreateAndLinkProperty({
	categoryId,
	directLinks,
	onDone,
}: {
	categoryId: string;
	directLinks: CategoryPropertyLink[];
	onDone: () => void;
}) {
	const { t } = useTranslation("categories");
	const { VALUE_TYPES } = useValueTypes();
	const [name, setName] = useState("");
	const [valueType, setValueType] = useState("string");
	const [unit, setUnit] = useState("");
	const [filterable, setFilterable] = useState(false);

	const createAndLinkMut = useMutation({
		mutationFn: async () => {
			const created = await client.createPropertyDefinition({
				name,
				slug: "",
				valueType,
				unit,
				isFilterable: filterable,
				isRequired: false,
			});

			const newLinks = [
				...directLinks.map((l, i) => ({
					categoryId,
					propertyDefinitionId: l.propertyDefinitionId,
					isRequired: l.isRequired,
					sortOrder: i,
					inherited: false,
					sourceCategoryId: "",
					sourceCategoryName: "",
				})),
				{
					categoryId,
					propertyDefinitionId: created.id,
					isRequired: false,
					sortOrder: directLinks.length,
					inherited: false,
					sourceCategoryId: "",
					sourceCategoryName: "",
				},
			];

			await client.setCategoryProperties({
				categoryId,
				properties: newLinks,
			});
		},
		onSuccess: () => onDone(),
	});

	return (
		<div className="p-3 space-y-2">
			<Input
				placeholder={t("name_placeholder")}
				value={name}
				onChange={(e) => setName(e.target.value)}
				className="h-7 text-xs"
				autoFocus
			/>
			<Select value={valueType} onValueChange={setValueType}>
				<SelectTrigger className="h-7 text-xs">
					<SelectValue placeholder={t("type_placeholder")} />
				</SelectTrigger>
				<SelectContent>
					{VALUE_TYPES.map((t) => (
						<SelectItem key={t.value} value={t.value}>
							{t.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			<Input
				placeholder={t("unit_placeholder")}
				value={unit}
				onChange={(e) => setUnit(e.target.value)}
				className="h-7 text-xs"
			/>
			<Label className="flex items-center gap-2 text-xs cursor-pointer">
				<Checkbox
					checked={filterable}
					onCheckedChange={(v) => setFilterable(v === true)}
				/>
				{t("filterable_label")}
			</Label>
			{createAndLinkMut.isError && (
				<p className="text-xs text-destructive">
					{createAndLinkMut.error?.message ?? t("error_creating")}
				</p>
			)}
			<Button
				className="w-full h-7 text-xs"
				size="sm"
				disabled={!name || createAndLinkMut.isPending}
				onClick={() => createAndLinkMut.mutate()}
			>
				{createAndLinkMut.isPending ? t("creating") : t("create_and_link")}
			</Button>
		</div>
	);
}
